import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  // NOTE: withCredentials removed intentionally.
  // Production domain (landflow.wefindland.com) makes cross-origin API calls.
  // The CORS proxy returns Access-Control-Allow-Origin: * which is incompatible
  // with withCredentials:true. We use X-Session-Token header instead of cookies.
});

// Add session token from localStorage via custom header (CORS-safe, no cookie needed)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['X-Session-Token'] = token;
  }
  // Admin impersonation: if active, attach target email so backend
  // returns the impersonated investor's data (read-only).
  const impersonateEmail = localStorage.getItem('impersonate_email');
  if (impersonateEmail) {
    config.headers['X-Impersonate-Email'] = impersonateEmail;
  }
  return config;
});

// Projects
export const projectsApi = {
  getAll: (status) => api.get('/projects', { params: status ? { status } : {} }),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  updateStatus: (id, status) => api.patch(`/projects/${id}/status`, null, { params: { status } }),
  getFinancialSummary: (id) => api.get(`/projects/${id}/financial-summary`),
  getProfitInfo: (id) => api.get(`/projects/${id}/profit-info`),
  getDisbursements: (id) => api.get(`/projects/${id}/disbursements`),
  getDisbursementSummary: (id) => api.get(`/projects/${id}/disbursement-summary`),
  createDisbursement: (id, data) => api.post(`/projects/${id}/disbursements`, data),
  downloadWorkbook: (id) => api.get(`/projects/${id}/workbook-reconciliation-xlsx`, { responseType: 'blob' }),
  workbookEmailPreview: (id, data) => api.post(`/projects/${id}/workbook-email/preview`, data || {}),
  sendWorkbookEmail: (id, data) => api.post(`/projects/${id}/workbook-email/send`, data),
};

// Subdivide Master — lot matrix, cap table, staggered-sale closing events
export const subdivideApi = {
  getCapTable: (projectId) => api.get(`/projects/${projectId}/cap-table`),
  getLots: (projectId) => api.get(`/projects/${projectId}/lots`),
  createLot: (projectId, data) => api.post(`/projects/${projectId}/lots`, data),
  updateLot: (lotId, data) => api.put(`/lots/${lotId}`, data),
  deleteLot: (lotId) => api.delete(`/lots/${lotId}`),
  getClosings: (projectId) => api.get(`/projects/${projectId}/closings`),
  previewClosing: (projectId, data) => api.post(`/projects/${projectId}/closings/preview`, data),
  createClosing: (projectId, data) => api.post(`/projects/${projectId}/closings`, data),
  updateClosing: (closingId, data) => api.put(`/closings/${closingId}`, data),
  deleteClosing: (closingId) => api.delete(`/closings/${closingId}`),
  getClosingCommissions: (projectId) => api.get(`/projects/${projectId}/closing-commissions`),
  convertToMaster: (parentId, dryRun = false) => api.post(`/projects/${parentId}/convert-to-master?dry_run=${dryRun}`),
};

// Auth
export const authApi = {
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  createSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getAuthorizedEmails: () => api.get('/auth/authorized-emails'),
  addAuthorizedEmail: (data) => api.post('/auth/authorized-emails', data),
  updateAuthorizedEmail: (email, data) => api.put(`/auth/authorized-emails/${encodeURIComponent(email)}`, data),
  removeAuthorizedEmail: (email) => api.delete(`/auth/authorized-emails/${encodeURIComponent(email)}`),
  sendResetLink: (email) => api.post(`/auth/send-reset-link/${encodeURIComponent(email)}`),
  sendResetLinkAll: () => api.post('/auth/send-reset-link-all'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Admin
export const adminApi = {
  triggerMonthlyDraw: (drawDay) => api.post(`/admin/trigger-monthly-draw?draw_day=${drawDay}`),
  getDrawRunLog: () => api.get('/admin/draw-run-log'),
  downloadMetricAudit: () => api.get('/admin/metric-audit-xlsx', { responseType: 'blob' }),
  // Wave-4 follow-up: QBO ↔ LandFlow reconciliation
  runQboReconciliation: (params = {}) => api.get('/admin/qbo-reconciliation/run', { params }),
  emailQboReconciliation: (params = {}) => api.post('/admin/qbo-reconciliation/email', null, { params }),
  getQboReconciliationHistory: (limit = 12) => api.get('/admin/qbo-reconciliation/history', { params: { limit } }),
};

// Transactions
export const transactionsApi = {
  getAll: (projectId, includeChildren = false) => api.get('/transactions', {
    params: {
      ...(projectId ? { project_id: projectId } : {}),
      ...(includeChildren ? { include_children: true } : {}),
    },
  }),
  create: (data) => api.post('/transactions', data),
  purchaseSplit: (data) => api.post('/transactions/purchase-split', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
  importQBO: (projectId, data) => api.post(`/projects/${projectId}/import-qbo`, data),
};

// JV Agreements
export const jvAgreementsApi = {
  getAll: (projectId) => api.get('/jv-agreements', { params: projectId ? { project_id: projectId } : {} }),
  create: (data) => api.post('/jv-agreements', data),
  update: (id, data) => api.put(`/jv-agreements/${id}`, data),
  delete: (id) => api.delete(`/jv-agreements/${id}`),
  updateSigningStatus: (id, data) => api.patch(`/jv-agreements/${id}/signing-status`, data),
  uploadSigned: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/jv-agreements/${id}/upload-signed`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Investor POF Upload
export const investorApi = {
  uploadPof: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/investor/upload-pof', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  adminViewPortal: (partnerId) => api.get(`/admin/investor-view/${partnerId}`),
  adminViewPortalByEmail: (email) => api.get(`/admin/investor-view-by-email/${encodeURIComponent(email)}`),
};

// Employee Ledger
export const ledgerApi = {
  getAll: (employeeId, year) => api.get('/employee-ledger', { params: { employee_id: employeeId, year } }),
  getSummary: (year) => api.get('/employee-ledger/summary', { params: year ? { year } : {} }),
  create: (data) => api.post('/employee-ledger', data),
  delete: (id) => api.delete(`/employee-ledger/${id}`),
  recalculateBalances: () => api.post('/employee-ledger/recalculate-balances'),
  getProjectCommissionStatus: (projectId) => api.get(`/employee-ledger/project/${projectId}/commission-status`),
};

// Employees
export const employeesApi = {
  getAll: (activeOnly = true) => api.get('/employees', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  getLedgerSummary: (employeeId, year) => api.get(`/employees/${employeeId}/ledger-summary`, { params: year ? { year } : {} }),
  // Commission management
  recordCommission: (data) => api.post('/employees/record-commission', data),
  recordMonthlyDraw: (data) => api.post('/employees/record-monthly-draw', data),
  generateMonthlyDraws: () => api.post('/employees/generate-monthly-draws'),
  previewCommission: (employeeId, netProfit) => api.get(`/employees/${employeeId}/commission-preview`, { params: { net_profit: netProfit } }),
};

// Owners
export const ownersApi = {
  getAll: (activeOnly = true) => api.get('/owners', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/owners', data),
  update: (id, data) => api.put(`/owners/${id}`, data),
  delete: (id) => api.delete(`/owners/${id}`),
  getSummary: () => api.get('/ownership/summary'),
};

// Profit Allocations
export const profitAllocationsApi = {
  getAll: (projectId) => api.get('/profit-allocations', { params: projectId ? { project_id: projectId } : {} }),
  create: (data) => api.post('/profit-allocations', data),
  update: (id, data) => api.put(`/profit-allocations/${id}`, data),
  delete: (id) => api.delete(`/profit-allocations/${id}`),
};

// Analytics
export const analyticsApi = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getCapitalByParty: () => api.get('/analytics/capital-by-party'),
  getPipelineInsights: () => api.get('/analytics/pipeline-insights'),
  getCapitalForecast: () => api.get('/analytics/capital-forecast'),
  getDealPipeline: () => api.get('/analytics/deal-pipeline'),
};

// Calculations
export const calculationsApi = {
  commission: (netProfit, ytdEarnings) => 
    api.post('/calculate/commission', null, { params: { net_profit: netProfit, ytd_earnings: ytdEarnings } }),
  roiIrr: (projectId) => api.get(`/reports/roi-irr/${projectId}`),
  capitalPosition: (projectId) => api.get(`/projects/${projectId}/capital-position`),
  partnerPositions: (projectId) => api.get(`/projects/${projectId}/partner-positions`),
  scenarioProjection: (projectId, scenario) =>
    api.post(`/projects/${projectId}/scenario-projection`, scenario),
  finalizeSalePreflight: (projectId) =>
    api.get(`/projects/${projectId}/finalize-sale/preflight`),
  finalizeSale: (projectId, payload) =>
    api.post(`/projects/${projectId}/finalize-sale`, payload),
};

// Subdivisions
export const subdivisionsApi = {
  create: (data) => api.post('/subdivisions/create', data),
  getChildren: (parentProjectId) => api.get(`/subdivisions/${parentProjectId}`),
  updateAllocation: (childProjectId, data) => api.put(`/subdivisions/${childProjectId}/allocation`, data),
  updateChildValue: (childProjectId, data) => api.put(`/subdivisions/${childProjectId}/value`, data),
  updateChildSaleStatus: (childProjectId, data) => api.put(`/subdivisions/${childProjectId}/sale-status`, data),
  getConsolidatedFinancials: (parentProjectId) => api.get(`/subdivisions/${parentProjectId}/consolidated-financials`),
};

// Owner Financing / Notes
export const notesApi = {
  create: (data) => api.post('/notes', data),
  getAll: (projectId, status) => api.get('/notes', { params: { project_id: projectId, status } }),
  getOne: (noteId) => api.get(`/notes/${noteId}`),
  update: (noteId, data) => api.put(`/notes/${noteId}`, data),
  delete: (noteId) => api.delete(`/notes/${noteId}`),
  sellNote: (noteId, data) => api.post(`/notes/${noteId}/sell`, data),
  recordPayment: (noteId, data) => api.post(`/notes/${noteId}/payments`, data),
  recordPaymentWithCommission: (noteId, data) => api.post(`/notes/${noteId}/payments-with-commission`, data),
  getPayments: (noteId) => api.get(`/notes/${noteId}/payments`),
  deletePayment: (noteId, paymentId) => api.delete(`/notes/${noteId}/payments/${paymentId}`),
  getSummary: () => api.get('/notes/summary/all'),
  getReport: () => api.get('/notes/report'),
  getCommissionStatus: (noteId) => api.get(`/notes/${noteId}/commission-status`),
};

// CSV Import
export const importApi = {
  projects: (data) => api.post('/import/projects', data),
  transactions: (data) => api.post('/import/transactions', data),
  jvAgreements: (data) => api.post('/import/jv-agreements', data),
};

// Reports
export const reportsApi = {
  getDeadDeals: (params = {}) => {
    const query = new URLSearchParams();
    if (params.period) query.append('period', params.period);
    if (params.year) query.append('year', params.year);
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    const qs = query.toString();
    return api.get(`/reports/dead-deals${qs ? '?' + qs : ''}`);
  },
  // Financial Reports
  getPnL: (params) => api.get('/reports/pnl', { params }),
  getYtdSummary: (year) => api.get('/reports/ytd-summary', { params: year ? { year } : {} }),
  getCashFlowProjection: (months) => api.get('/reports/cash-flow-projection', { params: months ? { months_ahead: months } : {} }),
  getInvestorStatement: (investorName) => api.get(`/reports/investor-statement/${encodeURIComponent(investorName)}`),
  getRoiIrr: (projectId) => api.get(`/reports/roi-irr/${projectId}`),
  // Canonical Phase-B report endpoints — all report math is server-side
  getPortfolio: (params = {}) => api.get('/reports/portfolio', { params }),
  getCapital: (params = {}) => api.get('/reports/capital', { params }),
};

// Documents
export const documentsApi = {
  getProjectDocuments: (projectId) => api.get(`/documents/project/${projectId}`),
  uploadDocument: (projectId, file, documentType = 'jv_agreement', description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('description', description);
    return api.post(`/documents/project/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocument: (documentId) => api.get(`/documents/${documentId}/download`, { responseType: 'blob' }),
  deleteDocument: (documentId) => api.delete(`/documents/${documentId}`),
};

// QuickBooks
// Notifications
export const notificationsApi = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  getProjectSettings: (projectId) => api.get(`/notifications/project/${projectId}`),
  updateProjectSettings: (projectId, data) => api.put(`/notifications/project/${projectId}`, data),
  checkAging: () => api.get('/notifications/aging-check'),
  sendDigest: () => api.post('/notifications/send-digest'),
  sendAlert: (projectId) => api.post(`/notifications/send-alert/${projectId}`),
  sendTestEmail: (recipient) => api.post('/notifications/test-email', null, { params: { recipient } }),
  getLogs: (limit = 50) => api.get('/notifications/logs', { params: { limit } }),
  // Scheduler
  getSchedulerStatus: () => api.get('/notifications/scheduler/status'),
  startScheduler: () => api.post('/notifications/scheduler/start'),
  stopScheduler: () => api.post('/notifications/scheduler/stop'),
  // Investor notifications
  sendInvestorSoldNotification: (projectId) => api.post(`/notifications/send-investor-notification/${projectId}`),
  // Digest report config
  getDigestConfig: () => api.get('/notifications/digest-config'),
  updateDigestConfig: (data) => api.put('/notifications/digest-config', data),
  sendDigestReport: () => api.post('/notifications/send-digest-report'),
};

// QuickBooks
export const qboApi = {
  getAuthUrl: () => api.get('/quickbooks/auth-url'),
  getStatus: () => api.get('/quickbooks/status'),
  disconnect: () => api.post('/quickbooks/disconnect'),
  getClasses: () => api.get('/quickbooks/classes'),
  mapClass: (data) => api.post('/quickbooks/map-class', data),
  unmapClass: (qboClassId) => api.delete(`/quickbooks/unmap-class/${qboClassId}`),
  clearAllMappings: () => api.post('/quickbooks/clear-all-mappings'),
  getIgnoredClasses: () => api.get('/quickbooks/ignored-classes'),
  ignoreClass: (data) => api.post('/quickbooks/ignore-class', data),
  unignoreClass: (qboClassId) => api.post('/quickbooks/unignore-class', { qbo_class_id: qboClassId }),
  backfillPreview: () => api.get('/quickbooks/backfill-preview'),
  backfillClasses: (projectIds) => api.post('/quickbooks/backfill-classes', projectIds ? { project_ids: projectIds } : {}),
  autoMap: () => api.post('/quickbooks/auto-map'),
  getPendingSuggestions: () => api.get('/quickbooks/pending-suggestions'),
  acceptSuggestion: (data) => api.post('/quickbooks/accept-suggestion', data),
  dismissSuggestion: (data) => api.post('/quickbooks/dismiss-suggestion', data),
  sync: () => api.post('/quickbooks/sync'),
  testConnection: () => api.post('/quickbooks/test-connection'),
  debugInfo: () => api.get('/quickbooks/debug-info'),
  getFailedMappings: () => api.get('/quickbooks/failed-mappings'),
  resolveFailedMapping: (id, data) => api.post(`/quickbooks/resolve-failed/${id}`, data),
  ignoreFailedMapping: (id) => api.post(`/quickbooks/ignore-failed/${id}`),
  getUnmappedClasses: () => api.get('/quickbooks/unmapped-classes'),
  confirmTransaction: (id) => api.post(`/quickbooks/confirm-transaction/${id}`),
  confirmAllTransactions: (projectId) => api.post(`/quickbooks/confirm-all/${projectId}`),
  getSyncHistory: () => api.get('/quickbooks/sync-history'),
  getNotifications: () => api.get('/quickbooks/notifications'),
  markNotificationRead: (id) => api.post(`/quickbooks/notifications/${id}/read`),
  dismissAllNotifications: () => api.post('/quickbooks/notifications/dismiss-all'),
  reconcileCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/quickbooks/reconcile-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importMissing: (transactions) => api.post('/quickbooks/import-missing', { transactions }),
};

// Partners & JV Management
export const partnersApi = {
  list: (status) => api.get('/partners', { params: status ? { status } : {} }),
  get: (id) => api.get(`/partners/${id}`),
  create: (data) => api.post('/partners', data),
  update: (id, data) => api.put(`/partners/${id}`, data),
  delete: (id) => api.delete(`/partners/${id}`),
  promote: (id) => api.post(`/partners/${id}/promote`),
  uploadPOF: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/partners/${id}/upload-pof`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadPOF: (id) => api.get(`/partners/${id}/download-pof`, { responseType: 'blob' }),
};

// Proposals (Investment Calculator scenarios linked to projects)
export const proposalsApi = {
  list: (projectId) => api.get('/proposals', { params: projectId ? { project_id: projectId } : {} }),
  get: (id) => api.get(`/proposals/${id}`),
  create: (data) => api.post('/proposals', data),
  update: (id, data) => api.put(`/proposals/${id}`, data),
  delete: (id) => api.delete(`/proposals/${id}`),
};

// Public partnership intake (no auth)
export const publicApi = {
  applyPartnership: (data) => axios.post(`${BACKEND_URL}/api/partnerships/apply`, data),
  applyPartnershipWithFile: (formData) => axios.post(`${BACKEND_URL}/api/partnerships/apply-with-pof`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Deal Flow — Promote to Partners workflow
export const dealFlowApi = {
  // Admin
  promote: (data) => api.post('/promoted-deals', data),
  listPromoted: (status) => api.get('/promoted-deals', { params: status ? { status } : {} }),
  getPromoted: (id) => api.get(`/promoted-deals/${id}`),
  addPartners: (id, data) => api.post(`/promoted-deals/${id}/add-partners`, data),
  listApplications: (params) => api.get('/deal-applications', { params }),
  approveApplication: (id) => api.post(`/deal-applications/${id}/approve`),
  declineApplication: (id, data) => api.post(`/deal-applications/${id}/decline`, data || {}),
  setDealType: (dealId, dealType) => api.patch(`/promoted-deals/${dealId}/deal-type`, { deal_type: dealType }),
  // Multi-partner (Phase 2)
  getDealPartners: (dealId) => api.get(`/promoted-deals/${dealId}/partners`),
  getProjectPartners: (projectId) => api.get(`/projects/${projectId}/partners`),
  confirmAllocation: (dealId, appId, data) =>
    api.post(`/promoted-deals/${dealId}/applications/${appId}/confirm`, data || {}),
  commitDeal: (dealId) => api.post(`/promoted-deals/${dealId}/commit`),
  documentVault: (params) => api.get('/jv-agreements/document-vault', { params }),
  // Proposal attachments (PDFs)
  uploadAttachment: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/promoted-deals/upload-attachment', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  addAttachmentToDeal: (dealId, attachmentMeta) =>
    api.post(`/promoted-deals/${dealId}/attachments`, attachmentMeta),
  removeAttachmentFromDeal: (dealId, attachmentId) =>
    api.delete(`/promoted-deals/${dealId}/attachments/${attachmentId}`),
  attachmentDownloadUrl: (dealId, attachmentId) =>
    `${api.defaults.baseURL}/promoted-deals/${dealId}/attachments/${attachmentId}/download`,
  downloadAttachment: (dealId, attachmentId) =>
    api.get(`/promoted-deals/${dealId}/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  // Investor/Partner facing
  availableDeals: () => api.get('/investor/available-deals'),
  applyForDeal: (dealId, data) => api.post(`/investor/available-deals/${dealId}/apply`, data || {}),
  acceptDeal: (dealId, data) => api.post(`/investor/available-deals/${dealId}/accept`, data || {}),
  declineDeal: (dealId, data) => api.post(`/investor/available-deals/${dealId}/decline`, data || {}),
  getProfile: () => api.get('/investor/profile'),
  updateProfile: (data) => api.patch('/investor/profile', data),
};

// Contract Templates (manual contract drafting)
export const contractTemplatesApi = {
  list: () => api.get('/contract-templates'),
  create: (data) => api.post('/contract-templates', data),
  update: (id, data) => api.patch(`/contract-templates/${id}`, data),
  remove: (id) => api.delete(`/contract-templates/${id}`),
  getDealContract: (dealId) => api.get(`/promoted-deals/${dealId}/contract`),
  saveDealContract: (dealId, data) => api.put(`/promoted-deals/${dealId}/contract`, data),
  resetDealContract: (dealId) => api.post(`/promoted-deals/${dealId}/contract/reset`),
};

export const dealsApi = {
  list: (status) => api.get('/deals', { params: status ? { status } : {} }),
  get: (id) => api.get(`/deals/${id}`),
  create: (data) => api.post('/deals', data),
  update: (id, data) => api.put(`/deals/${id}`, data),
  publish: (id, data) => api.post(`/deals/${id}/publish`, data),
  invest: (id, data) => api.post(`/deals/${id}/invest`, data),
  approveRequest: (dealId, requestId, data) => api.post(`/deals/${dealId}/requests/${requestId}/approve`, data),
};

export const appSettingsApi = {
  get: () => api.get('/app-settings'),
  update: (data) => api.put('/app-settings', data),
};

export const dealAnalysesApi = {
  list: () => api.get('/deal-analyses'),
  get: (id) => api.get(`/deal-analyses/${id}`),
  create: (data) => api.post('/deal-analyses', data),
  update: (id, data) => api.put(`/deal-analyses/${id}`, data),
  delete: (id) => api.delete(`/deal-analyses/${id}`),
  duplicate: (id) => api.post(`/deal-analyses/duplicate/${id}`),
};

export const settlementApi = {
  get: (projectId, side = 'buy') => api.get(`/projects/${projectId}/settlement-statement`, { params: { side } }),
  save: (projectId, payload) => api.post(`/projects/${projectId}/settlement-statement`, payload),
  delete: (projectId, statementId) => api.delete(`/projects/${projectId}/settlement-statement/${statementId}`),
  suggestCategory: (label, side = 'buy') => api.post('/settlement-statements/suggest-category', { label, side }),
};

export default api;
